
import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Filters from "./components_1/Filters";
import Leaderboard from "./components_1/Leaderboard";
import DeveloperPieChart from "./components_1/DeveloperPieChart";
import SprintTrendGraph from "./components_1/SprintTrendGraph";

import { fetchLeaderboard } from "./services_1/api";
import "./App.css";
import AiChatWidget from './components_1/AiChatWidget';

import { calculateEfficiencyMetrics } from "./utils/efficiencyCalculator";

import ProjectBurndownChart from "./components_1/ProjectBurndownChart";

const parseTimestamp = (value) => {
  if (value === undefined || value === null || value === "") return null;
  if (typeof value === "number" && Number.isFinite(value)) {
    return value < 1e12 ? value * 1000 : value;
  }
  if (typeof value === "string") {
    const trimmedValue = value.trim();
    if (!trimmedValue) return null;
    const numericValue = Number(trimmedValue);
    if (Number.isFinite(numericValue)) {
      return numericValue < 1e12 ? numericValue * 1000 : numericValue;
    }
    const parsedValue = Date.parse(trimmedValue);
    return Number.isNaN(parsedValue) ? null : parsedValue;
  }
  return null;
};

function NewFlow() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [timeframe, setTimeframe] = useState("all");
  const [isDashboardLoading, setIsDashboardLoading] = useState(false);

  const [activeProjectId, setActiveProjectId] = useState(null);
  const [projectData, setProjectData] = useState(null);
  const [activeChartTab, setActiveChartTab] = useState(null);
  const [activeItemTypeTab, setActiveItemTypeTab] = useState(null);
  const [showSprintGraph, setShowSprintGraph] = useState(false);
  
  const [showBurndownChart, setShowBurndownChart] = useState(false); 
  const [projectBurndownData, setProjectBurndownData] = useState(null);
  
  const handleSelect = async (projectId) => {
    setIsDashboardLoading(true);
    try {
      setTimeframe("all");
      setActiveProjectId(projectId); 
      
      const result = await fetchLeaderboard(projectId,true); // Pass ss=true to trigger summary generation on the backend
      
      setData(result.itemDetails || []); 
      
      setProjectBurndownData(result.projectBurndownData || null);
      
      
      setProjectData(result); // Save the project data for AI analysis
    } catch (err) {
      console.error(err);
    } finally {
      setIsDashboardLoading(false);
    }
  };

  const baseItemList = useMemo(
    () => (Array.isArray(data) ? data : []),
    [data]
  );

  const filteredItems = useMemo(() => {
    if (!baseItemList.length) return [];
    if (timeframe === "all") return baseItemList;

    const now = new Date();
    const day = now.getDay();
    const adjustedDay = day === 0 ? 7 : day;

    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - (adjustedDay - 1));
    startOfWeek.setHours(0, 0, 0, 0);

    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 4);
    endOfWeek.setHours(23, 59, 59, 999);

    const startOfLastWeek = new Date(startOfWeek);
    startOfLastWeek.setDate(startOfWeek.getDate() - 7);

    const endOfLastWeek = new Date(startOfLastWeek);
    endOfLastWeek.setDate(startOfLastWeek.getDate() + 4);
    endOfLastWeek.setHours(23, 59, 59, 999);

    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    startOfMonth.setHours(0, 0, 0, 0);

    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const oneDay = 24 * 60 * 60 * 1000;

    return baseItemList.filter(item => {
      const time = parseTimestamp(item.completedAt);
      if (time === null) return false;

      switch (timeframe) {
        case "today":
          return time >= startOfToday;
        case "yesterday":
          return time >= startOfToday - oneDay && time < startOfToday;
        case "thisWeek":
          return time >= startOfWeek.getTime() && time <= endOfWeek.getTime();
        case "lastWeek":
          return time >= startOfLastWeek.getTime() && time <= endOfLastWeek.getTime();
        case "thisMonth":
          return time >= startOfMonth.getTime() && time <= now.getTime();
        default:
          return true;
      }
    });
  }, [baseItemList, timeframe]);

  const dynamicMetrics = useMemo(() => {
    const devMap = {};
    const sprintCounts = {};
    const itemTypeCounts = { bug: 0, task: 0, story: 0, other: 0 };
    const sprintTypeMap = {};

    filteredItems.forEach(item => {
      const dev = item.completedBy || "Unknown/External";
      const itemType = (item.type || "unknown").toLowerCase();
      const normalizedType = ['bug', 'task', 'story'].includes(itemType) ? itemType : 'other';

      if (!devMap[dev]) {
        devMap[dev] = { total: 0, types: { bug: 0, task: 0, story: 0, other: 0 } };
      }

      devMap[dev].total += 1;
      devMap[dev].types[normalizedType] += 1;
      itemTypeCounts[normalizedType] += 1;

      const sprint = item.sprintName || "No Sprint";
      sprintCounts[sprint] = (sprintCounts[sprint] || 0) + 1;

      if (!sprintTypeMap[sprint]) {
        sprintTypeMap[sprint] = { bug: 0, task: 0, story: 0, other: 0, total: 0 };
      }
      sprintTypeMap[sprint][normalizedType] += 1;
      sprintTypeMap[sprint].total += 1;
    });

    const developerLeaderboard = Object.entries(devMap).map(([name, data]) => ({
      name,
      count: data.total,
      breakdown: data.types // Passing the breakdown object downstream
    }));

    const formatForCharts = (obj) =>
      Object.entries(obj).map(([name, count]) => ({ name, count }));

    const itemTypeShare = formatForCharts(itemTypeCounts)
      .filter(entry => entry.count > 0)
      .map(entry => ({
        ...entry,
        name: entry.name.charAt(0).toUpperCase() + entry.name.slice(1)
      }));

    const sprintTypeBreakdown = Object.entries(sprintTypeMap)
      .map(([name, counts]) => ({ name, ...counts }))
      .sort((a, b) => b.total - a.total);

    const groupedSprintTypeBreakdown = {};
    sprintTypeBreakdown.forEach(sprint => {
      const match = sprint.name.match(/^[A-Za-z]+/);
      const rawType = match ? match[0] : "Other";
      const type = rawType.charAt(0).toUpperCase() + rawType.slice(1).toLowerCase();

      if (!groupedSprintTypeBreakdown[type]) {
        groupedSprintTypeBreakdown[type] = [];
      }
      groupedSprintTypeBreakdown[type].push(sprint);
    });

    const efficiencyData = calculateEfficiencyMetrics(filteredItems);

    return {
      totalItems: filteredItems.length,
      developerLeaderboard,
      sprintLeaderboard: formatForCharts(sprintCounts),
      itemTypeShare,
      sprintTypeBreakdown,
      overallEfficiency: efficiencyData.overallEfficiency, // ✅ Extracted perfectly
      sprintEfficiencyList: efficiencyData.sprintEfficiencyList,
      groupedSprintEfficiency: efficiencyData.groupedSprintEfficiency,
      groupedSprintTypeBreakdown // ✅ NEW if you want to use it in the UI
    };
  }, [filteredItems]);

  const developerLeaderboard = dynamicMetrics.developerLeaderboard;
  const sprintLeaderboard = dynamicMetrics.sprintLeaderboard;
  const itemTypeShare = dynamicMetrics.itemTypeShare;
  const totalItems = dynamicMetrics.totalItems;

  const sortedLeaderboard = [...developerLeaderboard].sort((a, b) => b.count - a.count);

  const sprintGroups = dynamicMetrics.groupedSprintEfficiency || {};
  const groupNames = Object.keys(sprintGroups);
  // Default to the first group (e.g., "Sprint") if no tab is clicked yet
  const currentGroup = activeChartTab && groupNames.includes(activeChartTab) ? activeChartTab : groupNames[0];
  const currentChartData = currentGroup ? sprintGroups[currentGroup] : [];
  
  // 🔥 NEW: Variables for the Item-Type Share tabs
  const sprintTypeGroups = dynamicMetrics.groupedSprintTypeBreakdown || {};
  const itemTypeGroupNames = Object.keys(sprintTypeGroups);
  const currentItemTypeGroup = activeItemTypeTab && itemTypeGroupNames.includes(activeItemTypeTab) 
    ? activeItemTypeTab 
    : itemTypeGroupNames[0];
  const currentSprintTypeData = currentItemTypeGroup ? sprintTypeGroups[currentItemTypeGroup] : [];
  

  return (
    <div className="dashboard-shell">
      <div className="dashboard-aura aura-one" />
      <div className="dashboard-aura aura-two" />

      <div className="dashboard-container">
        <button
          type="button"
          className="dashboard-back-button"
          onClick={() => navigate("/")}
        >
          <span aria-hidden="true">←</span>
          <span>Back to Landing</span>
        </button>

        <section className="dashboard-header card">
          <div className="dashboard-header-copy">
            <p className="eyebrow">Zoho Sprint Intelligence</p>
            <h1>Project Analytics Dashboard</h1>
            <p className="dashboard-description">
              Transform raw sprint completions into a sharper command center for
              productivity, ownership, and volume trends across tasks, stories, and bugs.
            </p>
          </div>

          <div className="header-stat-cluster">
            <div className="header-stat">
              <span className="header-stat-label">Focus</span>
              <strong>Leaderboard clarity</strong>
              <p>See contribution patterns instantly.</p>
            </div>
            <div className="header-stat accent-stat">
              <span className="header-stat-label">View</span>
              <strong>Sprint-level completion share</strong>
              <p>Compare counts and distribution at a glance.</p>
            </div>
          </div>
        </section>

        <section className="control-deck card">
          <div className="control-copy">
            <p className="section-kicker">Launch Analysis</p>
            <h2>Choose a project to generate the board</h2>
            <p>
              Select a project below. This layer aggregates all completed items
              for a comprehensive visual experience.
            </p>
          </div>

          <Filters onSelect={handleSelect} isSubmitting={isDashboardLoading} />
        </section>

        {!data && (
          <section className="empty-state card">
            <div className="empty-state-badge">Ready for lift-off</div>
            <h2>Select a project to reveal the dashboard</h2>
            <p>
              Your data pipeline is already working. Once you load a project,
              the leaderboard, total completion counts, and developer breakdowns will appear.
            </p>
          </section>
        )}

        {data && (
          <div className="dashboard-content">
            <div className="card" style={{ padding: '16px 28px', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
              <span className="filter-label" style={{ margin: 0 }}>Timeframe:</span>
              <select
                className="select-input"
                style={{ minHeight: '40px', width: 'auto', padding: '0 16px', fontSize: '0.9rem' }}
                value={timeframe}
                onChange={(e) => setTimeframe(e.target.value)}
              >
                <option value="all">All Completed Items</option>
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="thisWeek">This Week [Mon - Fri]</option>
                <option value="lastWeek">Past Week [Mon - Fri]</option>
                <option value="thisMonth">This Month</option>
              </select>
              <span className="section-muted" style={{ marginLeft: 'auto', fontSize: '0.9rem' }}>
                Showing {totalItems} completed items
              </span>

              <button
                onClick={() => setShowBurndownChart(!showBurndownChart)}
                className="dashboard-back-button"
                style={{ margin: 0, marginLeft: '16px', padding: '8px 16px', border: '1px solid var(--accent-teal)', color: 'var(--accent-teal)' }}
              >
                {showBurndownChart ? "🔙 Back to Dashboard" : "📉 View Project Burndown"}
              </button>
            </div>

            {showBurndownChart ? (
               // IF TRUE: Render the new Burndown Chart full width
               <ProjectBurndownChart data={projectBurndownData} />
            ) : (


            <div className="dashboard-grid">
              <div className="kpi-row">
                <div className="metric-widget card">
                  <p className="metric-title">Total Completed</p>
                  <p className="metric-value">{totalItems}</p>
                  <p className="metric-footnote">Captured for the selected timeframe.</p>
                </div>

                <div className="insight-card card">
                  <div className="insight-pill">Delivery Efficiency</div>
                  <div className="insight-grid" style={{ gridTemplateColumns: '1fr' }}>
                    <div>
                      <span className="insight-label">Overall Project Completion</span>
                      <strong style={{ fontSize: '2.8rem', color: 'var(--accent-teal)' }}>
                        {dynamicMetrics.overallEfficiency !== null 
                          ? `${dynamicMetrics.overallEfficiency}%` 
                          : "N/A"}
                      </strong>
                    </div>
                  </div>
                </div>
              </div>

              <div className="charts-row">
                <div className="card chart-card">
                  <div className="chart-card-header">
                    <h3>Developer Completion Share</h3>
                  </div>
                  <DeveloperPieChart data={sortedLeaderboard} label="Completions" />
                </div>

                <div className="card chart-card">
                  <div className="chart-card-header">
                    <h3>Item Type Share</h3>
                  </div>
                  <DeveloperPieChart data={itemTypeShare} label="Items" />
                </div>
              </div>

              <div className="charts-row">
                <div className="card chart-card">
                  <div className="chart-card-header" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '12px' }}>
                    <h3 style={{ margin: 0 }}>Sprint Efficiency</h3>
                    
                    {/* 🔥 NEW: Render Category Tabs if there's more than 1 type */}
                    {groupNames.length > 1 && (
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        {groupNames.map(name => (
                          <button
                            key={name}
                            onClick={() => setActiveChartTab(name)}
                            className="breakdown-pill"
                            style={{
                              border: 'none',
                              cursor: 'pointer',
                              background: currentGroup === name ? 'rgba(86, 199, 221, 0.2)' : 'rgba(159, 223, 255, 0.08)',
                              color: currentGroup === name ? '#56c7dd' : 'var(--ink-soft)',
                              fontWeight: currentGroup === name ? 'bold' : 'normal',
                              transition: 'all 0.2s ease'
                            }}
                          >
                            {name} Cycle
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* The chart dynamically updates based on the selected tab */}
                  <DeveloperPieChart 
                    data={currentChartData} 
                    label="Efficiency %" 
                    disableSort={true} /* 🔥 NEW: Tell the chart to stop sorting! */
                  />
                </div>

                <div className="card sprint-breakdown-card">
                  {/* 🔥 UPDATED HEADER: Flexbox pushes the button to the extreme right */}
                  <div className="chart-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
                    
                    {/* Left Side: Title and Tabs */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '12px' }}>
                      <h3 style={{ margin: 0 }}>Sprint Item-Type Share</h3>
                      {itemTypeGroupNames.length > 1 && (
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          {itemTypeGroupNames.map(name => (
                            <button
                              key={name}
                              onClick={() => setActiveItemTypeTab(name)}
                              className="breakdown-pill"
                              style={{
                                border: 'none',
                                cursor: 'pointer',
                                background: currentItemTypeGroup === name ? 'rgba(86, 199, 221, 0.2)' : 'rgba(159, 223, 255, 0.08)',
                                color: currentItemTypeGroup === name ? '#56c7dd' : 'var(--ink-soft)',
                                fontWeight: currentItemTypeGroup === name ? 'bold' : 'normal',
                                transition: 'all 0.2s ease'
                              }}
                            >
                              {name} Cycle
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Right Side: Toggle Button */}
                    <button
                      onClick={() => setShowSprintGraph(!showSprintGraph)}
                      className="dashboard-back-button" // Reusing your nice pill button style
                      style={{ margin: 0, padding: '8px 16px', fontSize: '0.85rem', minWidth: '120px', justifyContent: 'center' }}
                    >
                      {showSprintGraph ? '📊 View List' : '📈 View Graph'}
                    </button>
                  </div>

                  {/* 🔥 CONDITIONAL RENDERING: Show Graph OR the List */}
                  {showSprintGraph ? (
                    <SprintTrendGraph data={currentSprintTypeData} />
                  ) : (
                    <div className="sprint-breakdown-shell" style={{ maxHeight: '400px', paddingRight: '8px' }}>
                      {currentSprintTypeData.length === 0 ? (
                        <p className="chart-empty">No sprint breakdown available.</p>
                      ) : (
                        <div className="sprint-breakdown-grid">
                          {currentSprintTypeData.map((sprint) => (
                            <div key={sprint.name} className="sprint-breakdown-row">
                              <div className="sprint-row-header">
                                <span className="sprint-name">{sprint.name}</span>
                                <span className="sprint-total-badge">{sprint.total} Items</span>
                              </div>

                              <div className="sprint-row-detail">
                                <span className="breakdown-pill task-pill">
                                  <span className="pill-icon">🎯</span> Tasks <strong className="pill-count">{sprint.task}</strong>
                                </span>
                                <span className="breakdown-pill story-pill">
                                  <span className="pill-icon">📖</span> Stories <strong className="pill-count">{sprint.story}</strong>
                                </span>
                                <span className="breakdown-pill bug-pill">
                                  <span className="pill-icon">🐛</span> Bugs <strong className="pill-count">{sprint.bug}</strong>
                                </span>
                                {sprint.other > 0 && (
                                  <span className="breakdown-pill other-pill">
                                    <span className="pill-icon">❓</span> Other <strong className="pill-count">{sprint.other}</strong>
                                  </span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="leaderboard-row">
                <Leaderboard
                  title="Developer with highest completed items"
                  subtitle="Ranked by completions with item breakdown"
                  data={sortedLeaderboard}
                  sortDirection="desc" 
                  variant="developer"
                  valueLabel="Completions"
                />

                <Leaderboard
                  title="Sprint Leaderboard"
                  subtitle="Completions grouped by Sprint"
                  data={sprintLeaderboard}
                  variant="epic"
                  sortDirection="desc"
                  valueLabel="Items"
                />
              </div>
            </div>
          )}
          </div>
        )}
      </div>
      <AiChatWidget projectId={data ? activeProjectId : null} projectData={projectData} /> 
    </div>
  );
}

export default NewFlow;
